// „Занули склада" — чистата сметка (без React; въпросът и прилагането са в
// `useStocktakeZeroing`). Кой продукт получава „Намерено" 0: всеки НЕпреброен
// ред на листа със ЗНАКОВА наличност ≠ 0 към датата — и минусите (в „Търсено"
// те се четат като 0, а BE ги вдига до 0 като „Равно"), и изтритите в
// организацията с наличност (те вече са редове, виж
// `useStocktakeCountableProducts`). Вече написаното „Намерено" не се пипа,
// продуктите на 0 не получават ред, а филтрите на изгледа не ограничават
// избора — редовете идват от `allRows`, не от показаните.
import type { StockBalance } from "../../../../api/inventory";
import {
  DIFF_EPSILON,
  emptyStocktakeRow,
  formatQtyInput,
  isRowCounted,
  type StocktakeComputedRow,
  type StocktakeRowDraft,
} from "./stocktakeWorksheetShared";

export type ZeroingCandidate = {
  productId: number;
  name: string;
  quantity: number; // знаковата наличност към датата (≠ 0)
  unit: string;
  decimals: number;
  // ориентировъчна ед. цена: средно претеглената, иначе последната доставна
  unitPrice: number | null;
};

export type ZeroingPlan = {
  all: ZeroingCandidate[]; // всички непреброени с наличност ≠ 0
  negative: ZeroingCandidate[]; // само тези на минус
  alreadyCounted: number; // преброените редове — остават както са
  // Σ положителна наличност × ед. цена; null — нито един продукт няма цена
  estimatedShortage: number | null;
  unpricedCount: number; // продукти с положителна наличност без цена
};

const priceOf = (balance: StockBalance | undefined): number | null => {
  const price = balance?.weightedAvgPrice ?? balance?.lastDeliveryPrice ?? null;
  return price == null ? null : Number(price);
};

export const buildZeroingPlan = (
  rows: StocktakeComputedRow[],
  balanceByProduct: Map<number, StockBalance>,
): ZeroingPlan => {
  const all: ZeroingCandidate[] = [];
  let alreadyCounted = 0;
  for (const row of rows) {
    if (isRowCounted(row.draft)) {
      alreadyCounted += 1;
      continue;
    }
    const balance = balanceByProduct.get(row.product.id);
    const quantity = Number(balance?.quantity ?? 0);
    if (Math.abs(quantity) <= DIFF_EPSILON) continue;
    all.push({
      productId: row.product.id,
      name: row.product.name,
      quantity,
      unit: row.unit,
      decimals: row.decimals,
      unitPrice: priceOf(balance),
    });
  }
  let estimatedShortage: number | null = null;
  let unpricedCount = 0;
  for (const candidate of all) {
    if (candidate.quantity <= 0) continue;
    if (candidate.unitPrice == null) {
      unpricedCount += 1;
      continue;
    }
    estimatedShortage = (estimatedShortage ?? 0) + candidate.quantity * candidate.unitPrice;
  }
  return {
    all,
    negative: all.filter((candidate) => candidate.quantity < 0),
    alreadyCounted,
    estimatedShortage,
    unpricedCount,
  };
};

// Редовете на листа след зануляването: избраните получават „Намерено" 0 (във
// формата на полето — „0" / „0,000" по мерната единица), стойността на реда се
// извежда наново; всичко останало е непокътнато.
export const zeroedRows = (
  rows: Record<number, StocktakeRowDraft>,
  candidates: ZeroingCandidate[],
): Record<number, StocktakeRowDraft> => {
  const next = { ...rows };
  for (const candidate of candidates) {
    next[candidate.productId] = {
      ...(rows[candidate.productId] ?? emptyStocktakeRow()),
      counted: formatQtyInput(0, candidate.decimals),
      net: "",
      gross: "",
    };
  }
  return next;
};
